import { useMemo } from 'react';

import { RichtextDisplay } from '../../teaching-notes/teaching-notes-drawer.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  CONTAINER_WIDTH,
  SUGGESTED_ANSWER_BASE_RICHTEXT,
} from '../suggested-answer.constants';
import { ComponentMultipleChoice } from '../../component.constants';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { COMPONENT_SELECTOR } from '../../shared/logic/get-component-selector-id';
import { useFindComponentDimensions } from '../../component.hooks';
import MultipleChoicePrint from '../../multiple-choice/multiple-choice-print';
import SuggestedAnswerTitle from '../suggested-answer-title';
import {
  getRichtextValue,
  isRichtextValueEmpty,
} from '../../shared/logic/get-richtext-value';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityPresentModal from 'approot/activity-builder/present/activity-present-modal';
import { getSubActivityLabel } from '../suggested-answer.utils';
import { useGetRichtextAnswer } from '../suggested-answer.hooks';
import SuggestedAnswerPreview from './suggested-answer-preview';
import { SuggestedAnswerPreviewContainer } from '../suggested-answer.style';
import {
  ACTIVITIES_ANSWER_MODE_FREE,
  SUGGESTED_ANSWER_EXAMPLE,
  SUGGESTED_ANSWER_VARY,
} from 'global/constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import SuggestedAnswerExampleRichtext from './suggested-answer-example-richtext';
import { getRichtextAnswer } from '../../shared/logic/get-richtext-answer';
import SuggestedAnswerViewBadges from './suggested-answer-view-badges';

type Props = {
  component: ComponentMultipleChoice;
};

const SuggestedAnswerMultipleChoiceView = ({
  component,
  isUnitQuiz,
}: Props & {
  isUnitQuiz: boolean;
}) => {
  if (
    component.properties.multipleChoice.answerMode ===
    ACTIVITIES_ANSWER_MODE_FREE
  ) {
    return <FreeAnswerView component={component} isUnitQuiz={isUnitQuiz} />;
  }
  return <FixedAnswerView component={component} isUnitQuiz={isUnitQuiz} />;
};

export default SuggestedAnswerMultipleChoiceView;

const FreeAnswerView = ({
  component,
  isUnitQuiz,
}: Props & {
  isUnitQuiz: boolean;
}) => {
  const { id, subActivityProperties, suggestedAnswerProperties } = component;

  const answerType =
    suggestedAnswerProperties?.suggestedAnswerType || SUGGESTED_ANSWER_EXAMPLE;

  const hasNoRichTextData = useMemo(
    () =>
      answerType !== SUGGESTED_ANSWER_VARY &&
      suggestedAnswerProperties?.suggestedAnswers?.every(answer =>
        isRichtextValueEmpty(getRichtextAnswer(answer))
      ),
    [answerType, suggestedAnswerProperties]
  );

  const answer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  return hasNoRichTextData && !isUnitQuiz ? null : (
    <Box>
      <SuggestedAnswerTitle
        isSmall
        label={subActivityProperties?.subActivityLabel}
        instructions={subActivityProperties?.instructions}
      />

      <SuggestedAnswerViewBadges
        isUnitQuiz={isUnitQuiz}
        suggestedAnswerScore={suggestedAnswerProperties?.suggestedAnswersScore}
        isExampleAnswer={
          answerType === SUGGESTED_ANSWER_EXAMPLE &&
          !isRichtextValueEmpty(answer)
        }
      />

      {answerType === SUGGESTED_ANSWER_VARY ? (
        <PStyled>Answers may vary</PStyled>
      ) : answerType === SUGGESTED_ANSWER_EXAMPLE ? (
        <SuggestedAnswerExampleRichtext
          id={id}
          suggestedAnswerProperties={suggestedAnswerProperties}
        />
      ) : null}
    </Box>
  );
};

const FixedAnswerView = ({
  component,
  isUnitQuiz,
}: Props & {
  isUnitQuiz: boolean;
}) => {
  const { id, subActivityProperties, suggestedAnswerProperties, properties } =
    component;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { width, height } = useFindComponentDimensions(id, COMPONENT_SELECTOR);

  const richtextAnswer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  const score = useMemo(
    () =>
      properties.multipleChoice.options.reduce(
        (acc, option) => (option.correctAnswerBoolean ? acc + 1 : acc),
        0
      ),
    [properties.multipleChoice.options]
  );

  return (
    <Box>
      <SuggestedAnswerTitle
        isSmall
        label={subActivityProperties?.subActivityLabel}
        instructions={subActivityProperties?.instructions}
      />

      {isUnitQuiz && (
        <SuggestedAnswerViewBadges
          suggestedAnswerScore={score}
          isUnitQuiz={isUnitQuiz}
        />
      )}

      {isVisible && (
        <ActivityPresentModal
          isVisible
          title="Viewing Answers"
          onClose={closeModal}
          size={width}
        >
          <SuggestedAnswerTitle
            isLarge
            label={subActivityProperties?.subActivityLabel}
            instructions={subActivityProperties?.instructions}
          />
          <ActivityBuilderComponentPreview
            container={{
              width: width - 64,
            }}
            content={{
              width,
              height,
            }}
          >
            <MultipleChoicePrint
              {...component}
              subActivityProperties={getSubActivityLabel(subActivityProperties)}
              previewSize={CONTAINER_WIDTH}
              showAnswer
            />
          </ActivityBuilderComponentPreview>
        </ActivityPresentModal>
      )}

      <SettingsGroup marginBottom={8}>
        <SuggestedAnswerPreviewContainer>
          <ActivityBuilderComponentPreview
            container={{
              width: CONTAINER_WIDTH,
            }}
            content={{
              width,
              height,
            }}
          >
            <MultipleChoicePrint
              {...component}
              subActivityProperties={getSubActivityLabel(subActivityProperties)}
              previewSize={CONTAINER_WIDTH}
            />
          </ActivityBuilderComponentPreview>
        </SuggestedAnswerPreviewContainer>

        <SuggestedAnswerPreview
          ariaLabel="View multiple choice response answers"
          onClick={openModal}
        />
      </SettingsGroup>

      {!isRichtextValueEmpty(richtextAnswer) && (
        <RichtextDisplay
          dangerouslySetInnerHTML={{
            __html: getRichtextValue(
              richtextAnswer,
              SUGGESTED_ANSWER_BASE_RICHTEXT
            ),
          }}
        />
      )}
    </Box>
  );
};
